import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { Stack, Row, Text, Panel, List, ListItem, Button, Input, EmptyState, Tag, Divider, BlacklistAlertBadge, KycUploadCard } from '../ui';

const ROLES = [
  { v: 'buyer', l: 'Buyer' },
  { v: 'seller', l: 'Seller' },
  { v: 'counterparty', l: 'Counterparty' },
  { v: 'guarantor', l: 'Guarantor' },
  { v: 'agent', l: 'Agent / Broker' },
];

export function DealParties({ dealId }: { dealId: string }) {
  const [parties, setParties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<any>({ role: 'buyer', legalName: '' });
  const [issuingFor, setIssuingFor] = useState<string | null>(null);
  const [tokens, setTokens] = useState<Record<string, any[]>>({});
  const [busy, setBusy] = useState(false);
  const [prefillArtifactId, setPrefillArtifactId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.partiesByDeal(dealId);
      setParties(r.parties);
      // load tokens for each party
      const t: Record<string, any[]> = {};
      for (const p of r.parties) {
        try { const tr = await api.partyAccessTokens(p.id); t[p.id] = tr.tokens; } catch {}
      }
      setTokens(t);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [dealId]);

  async function addParty() {
    if (!draft.legalName?.trim()) return;
    setBusy(true);
    try {
      const r = await api.addParty(dealId, draft);
      const newPartyId = r.id;
      // Retroactively link any prefill artifact
      if (prefillArtifactId && newPartyId) {
        api.linkArtifact(prefillArtifactId, { partyId: newPartyId }).catch(() => {});
        setPrefillArtifactId(null);
      }
      setDraft({ role: 'buyer', legalName: '' });
      await load();
    } finally { setBusy(false); }
  }

  async function patchAndClose(partyId: string, p: any) {
    setBusy(true);
    try {
      const { id, dealId, groupId, addedBy, createdAt, updatedAt, ...patch } = p;
      await api.patchParty(partyId, patch);
      setEditing(null);
      await load();
    } finally { setBusy(false); }
  }

  async function removeParty(partyId: string) {
    if (!confirm('Remove this party from the deal?')) return;
    await api.deleteParty(partyId);
    await load();
  }

  async function issueAccess(partyId: string, days: number) {
    setIssuingFor(partyId);
    try {
      const r = await api.issuePartyAccess(partyId, days);
      try { await navigator.clipboard.writeText(r.dashboardUrl); } catch {}
      await load();
      alert(`portal link copied to clipboard\n\nvalid for ${days} days\n\n${r.dashboardUrl}`);
    } finally { setIssuingFor(null); }
  }

  async function revoke(token: string) {
    if (!confirm('Revoke this portal link? Anyone holding it will lose access immediately.')) return;
    await api.revokePartyAccess(token);
    await load();
  }

  return (
    <Stack gap={3}>
      <Panel title="Parties on this deal">
        {loading ? <Text variant="label">loading…</Text> : parties.length === 0 ? (
          <EmptyState title="no parties yet" hint="add the buyer, seller, or any counterparty below — then issue secure portal links so they can sign the NDA, view documents, and upload" />
        ) : (
          <Stack gap={2}>
            {parties.map(p => {
              const isEd = editing === p.id;
              const partyTokens = tokens[p.id] ?? [];
              const activeTokens = partyTokens.filter((t: any) => !t.revokedAt && new Date(t.expiresAt).getTime() > Date.now());
              return (
                <div key={p.id} className={`hairline rounded-sm p-3 ${p.listFlagStatus === 'blacklist_hit' ? 'ring-1 ring-negative/50 bg-negative/5' : ''}`}>
                  <Row justify="between" align="start">
                    <Stack gap={0.5}>
                      <Row gap={1}>
                        <Tag>{p.role}</Tag>
                        {p.entityId ? (
                          <Link to={`/crm/${p.entityId}`} className="hover:underline">
                            <Text variant="small" className="font-medium">{p.legalName}</Text>
                          </Link>
                        ) : (
                          <Text variant="small" className="font-medium">{p.legalName}</Text>
                        )}
                        {p.listFlagStatus === 'blacklist_hit' && <BlacklistAlertBadge />}
                      </Row>
                      {p.companyName && p.companyName !== p.legalName && <Text variant="label">trading as {p.companyName}</Text>}
                      {p.contactName && <Text variant="label">{p.contactName}{p.contactTitle ? ` · ${p.contactTitle}` : ''}{p.contactEmail ? ` · ${p.contactEmail}` : ''}</Text>}
                      {p.jurisdiction && <Text variant="label">{p.jurisdiction}{p.registrationId ? ` · ${p.registrationId}` : ''}</Text>}
                    </Stack>
                    <Row gap={1}>
                      {!isEd && <Button variant="ghost" size="sm" onClick={() => setEditing(p.id)}>edit</Button>}
                      {!isEd && <Button variant="ghost" size="sm" onClick={() => removeParty(p.id)}>remove</Button>}
                    </Row>
                  </Row>

                  {isEd && (
                    <Stack gap={2} className="mt-2">
                      <PartyForm value={p} onChange={v => setParties(prev => prev.map(x => x.id === p.id ? v : x))} />
                      <Row gap={1}>
                        <Button variant="primary" size="sm" disabled={busy} onClick={() => patchAndClose(p.id, p)}>save</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(null); load(); }}>cancel</Button>
                      </Row>
                    </Stack>
                  )}

                  <Divider className="my-2" />
                  <KycUploadCard context="party" targetId={p.id} onAutofilled={() => load()} />
                  <Divider className="my-2" />
                  <Stack gap={1}>
                    <Text variant="micro">secure portal access</Text>
                    {activeTokens.length === 0 ? (
                      <Text variant="label" muted>no active link — issue one to give this party a no-account dashboard for status, document upload/download, and NDA signing</Text>
                    ) : (
                      <Stack gap={0.5}>
                        {activeTokens.map((t: any) => (
                          <Row key={t.token} gap={1} justify="between">
                            <input className="text-xs hairline rounded-sm px-2 py-1.5 bg-bg flex-1 tnum" readOnly value={t.dashboardUrl} onClick={e => (e.target as HTMLInputElement).select()} />
                            <Button variant="ghost" size="sm" onClick={() => navigator.clipboard.writeText(t.dashboardUrl)}>copy</Button>
                            <Button variant="ghost" size="sm" onClick={() => revoke(t.token)}>revoke</Button>
                          </Row>
                        ))}
                      </Stack>
                    )}
                    <Row gap={1}>
                      <Button variant="secondary" size="sm" disabled={issuingFor === p.id} onClick={() => issueAccess(p.id, 30)}>{issuingFor === p.id ? 'creating…' : 'create 30-day link'}</Button>
                      <Button variant="ghost" size="sm" disabled={issuingFor === p.id} onClick={() => issueAccess(p.id, 7)}>7-day link</Button>
                    </Row>
                  </Stack>
                </div>
              );
            })}
          </Stack>
        )}
      </Panel>

      <Panel title="Add a party">
        <Stack gap={2}>
          <KycUploadCard
            context="party-prefill"
            onPrefill={(profile, artId) => {
              setPrefillArtifactId(artId);
              setDraft((prev: any) => ({
                ...prev,
                legalName: profile.fullName || prev.legalName || '',
                contactTitle: profile.title || prev.contactTitle || '',
                jurisdiction: profile.country || prev.jurisdiction || '',
                address: profile.address || prev.address || '',
                contactEmail: profile.primaryEmail || prev.contactEmail || '',
                contactPhone: profile.primaryPhone || prev.contactPhone || '',
                registrationId: (profile.identifiers?.lei ?? profile.identifiers?.vat ?? profile.identifiers?.ein ?? profile.identifiers?.passport) || prev.registrationId || '',
                companyName: (profile.alsoKnownAs?.[0]) || prev.companyName || '',
              }));
            }}
          />
          <PartyForm value={draft} onChange={setDraft} />
          <Row gap={1}>
            <Button variant="primary" size="sm" disabled={busy || !draft.legalName?.trim()} onClick={addParty}>{busy ? 'adding…' : 'add party'}</Button>
          </Row>
        </Stack>
      </Panel>
    </Stack>
  );
}

function PartyForm({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  return (
    <Stack gap={2}>
      {/* Row 1: role + legal name + dba */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Stack gap={0.5}>
          <Text variant="label">role</Text>
          <select value={value.role} onChange={e => onChange({ ...value, role: e.target.value })} className="h-10 sm:h-9 hairline rounded-sm px-2 bg-bg text-sm">
            {ROLES.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </Stack>
        <Stack gap={0.5}>
          <Text variant="label">legal name *</Text>
          <Input value={value.legalName ?? ''} onChange={e => onChange({ ...value, legalName: e.target.value })} placeholder="e.g. Atlantic Trader BV" />
        </Stack>
        <Stack gap={0.5}>
          <Text variant="label">trading / d.b.a.</Text>
          <Input value={value.companyName ?? ''} onChange={e => onChange({ ...value, companyName: e.target.value })} />
        </Stack>
      </div>
      {/* Row 2: jurisdiction + reg id + address */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Stack gap={0.5}>
          <Text variant="label">jurisdiction</Text>
          <Input value={value.jurisdiction ?? ''} onChange={e => onChange({ ...value, jurisdiction: e.target.value })} placeholder="NL, NG, SG…" />
        </Stack>
        <Stack gap={0.5}>
          <Text variant="label">registration / VAT id</Text>
          <Input value={value.registrationId ?? ''} onChange={e => onChange({ ...value, registrationId: e.target.value })} />
        </Stack>
        <Stack gap={0.5}>
          <Text variant="label">address</Text>
          <Input value={value.address ?? ''} onChange={e => onChange({ ...value, address: e.target.value })} />
        </Stack>
      </div>
      {/* Row 3: contact fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Stack gap={0.5}>
          <Text variant="label">contact name</Text>
          <Input value={value.contactName ?? ''} onChange={e => onChange({ ...value, contactName: e.target.value })} />
        </Stack>
        <Stack gap={0.5}>
          <Text variant="label">contact email</Text>
          <Input type="email" value={value.contactEmail ?? ''} onChange={e => onChange({ ...value, contactEmail: e.target.value })} />
        </Stack>
        <Stack gap={0.5}>
          <Text variant="label">phone</Text>
          <Input value={value.contactPhone ?? ''} onChange={e => onChange({ ...value, contactPhone: e.target.value })} />
        </Stack>
        <Stack gap={0.5}>
          <Text variant="label">title</Text>
          <Input value={value.contactTitle ?? ''} onChange={e => onChange({ ...value, contactTitle: e.target.value })} />
        </Stack>
      </div>
      <Stack gap={0.5}>
        <Text variant="label">notes (private to your circle)</Text>
        <Input value={value.notes ?? ''} onChange={e => onChange({ ...value, notes: e.target.value })} />
      </Stack>
    </Stack>
  );
}
